import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { Address, erc20Abi, maxUint256 } from 'viem';
import { usePublicClient } from 'wagmi';

import { ClientInputError } from '@/lib/errors/client';

import { ETH_FAKE_ADDRESS } from '@/constants/addresses';

import { AllowanceParams } from '@/utils/0x.types';
import { EthereumAddress } from '@/utils/models';

import { useMatchaWallets } from './useMatchaWallets';

export const allowanceQueryKey = (
  tokenContractAddress: EthereumAddress | undefined,
  chainId: number | undefined,
  contractAddressToApprove: EthereumAddress | undefined,
) => ['allowance', tokenContractAddress, chainId, contractAddressToApprove] as const;

/**
 * Fetch allowance hook
 * @param tokenContractAddress - Token address to fetch an allowance
 * @param contractAddressToApprove - Contract to spend the token allowance
 * @param chainId - Chain ID for the allowance check
 */
export function useAllowance(
  tokenContractAddress: EthereumAddress | undefined,
  contractAddressToApprove: EthereumAddress | undefined,
  chainId: number | undefined,
) {
  const publicClient = usePublicClient({ chainId });
  const { ethereumWallet } = useMatchaWallets();

  const params: AllowanceParams | undefined = useMemo(() => {
    if (!ethereumWallet || !contractAddressToApprove || !publicClient || !tokenContractAddress)
      return;

    return {
      walletAddress: ethereumWallet.address as Address,
      publicClient,
      tokenContractAddress,
      contractAddressToApprove,
    };
  }, [ethereumWallet, contractAddressToApprove, publicClient, tokenContractAddress]);

  const allowanceFetcher = useCallback((): Promise<bigint> | bigint => {
    if (!params) throw new ClientInputError('fetcher params undefined');
    return params.tokenContractAddress.toLowerCase() === ETH_FAKE_ADDRESS
      ? maxUint256 // For native tokens, no approval is necessary, so return the max safe integer
      : params.publicClient.readContract({
          abi: erc20Abi,
          address: params.tokenContractAddress,
          functionName: 'allowance',
          args: [params.walletAddress, params.contractAddressToApprove],
        });
  }, [params]);

  const { data, error, refetch } = useQuery<bigint, Error>({
    queryKey: allowanceQueryKey(tokenContractAddress, chainId, contractAddressToApprove),
    queryFn: allowanceFetcher,
    enabled: !!params,
    refetchInterval: 20_000,
    placeholderData: keepPreviousData,
  });

  return {
    data,
    error,
    refetch,
  };
}
